import { SlideshowSelectEvent } from '@theme/events';

class DelsolBestSellers extends HTMLElement {
  #slideshow;
  #reducedMotion;

  #pause = () => {
    this.#slideshow?.pause();
  };

  #handleMotionChange = (event) => {
    if (event.matches) this.#pause();
  };

  #handleArrowClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('button[data-delsol-best-sellers-action]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled) return;

    const slides = this.#slideshow?.slides;
    if (!slides?.length) return;

    const direction = button.dataset.delsolBestSellersAction === 'next' ? 1 : -1;
    const targetIndex = this.#slideshow.current + direction;
    if (targetIndex < 0 || targetIndex >= slides.length) return;

    event.preventDefault();
    this.#slideshow.select(targetIndex, event);
  };

  #handleSlideSelect = () => {
    this.#updateArrowState();
  };

  #updateArrowState() {
    const slides = this.#slideshow?.slides;
    if (!slides?.length) return;

    const currentIndex = this.#slideshow.current;
    const visibleSlideCount = Math.max(this.#slideshow.visibleSlides?.length || 0, 1);
    const lastStartIndex = Math.max(slides.length - visibleSlideCount, 0);

    this.querySelectorAll('button[data-delsol-best-sellers-action]').forEach((button) => {
      button.disabled =
        button.dataset.delsolBestSellersAction === 'previous'
          ? currentIndex <= 0
          : currentIndex >= lastStartIndex;
    });
  }

  async connectedCallback() {
    if (!customElements.get('slideshow-component')) {
      await customElements.whenDefined('slideshow-component');
    }

    if (!this.isConnected) return;

    const slideshow = this.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement) || typeof slideshow.select !== 'function') return;

    this.#slideshow = slideshow;
    this.addEventListener('click', this.#handleArrowClick);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect);
    this.#updateArrowState();

    if (slideshow.hasAttribute('autoplay') && typeof slideshow.pause === 'function') {
      this.addEventListener('focusin', this.#pause);
      this.addEventListener('pointerdown', this.#pause);
      this.addEventListener('keydown', this.#pause);

      this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.#reducedMotion.addEventListener('change', this.#handleMotionChange);

      if (this.#reducedMotion.matches) this.#pause();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleArrowClick);
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('pointerdown', this.#pause);
    this.removeEventListener('keydown', this.#pause);
    this.#reducedMotion?.removeEventListener('change', this.#handleMotionChange);
    this.#slideshow = undefined;
    this.#reducedMotion = undefined;
  }
}

if (!customElements.get('delsol-best-sellers')) {
  customElements.define('delsol-best-sellers', DelsolBestSellers);
}
